<template>
  <section class="select-sheet">
    <header class="select-sheet__header">
      <h3 class="select-sheet__title">{{ title }}</h3>
    </header>

    <div v-if="searchable" class="select-sheet__search">
      <input
        ref="searchInput"
        v-model="search"
        type="text"
        :placeholder="searchPlaceholder || $t('search') || 'ค้นหา'"
        class="select-sheet__search-input"
        :data-test="`${dataTestPrefix}-search`"
      />
      <font-awesome-icon
        :icon="['fas', 'search']"
        class="select-sheet__search-icon"
      />
    </div>

    <div class="select-sheet__list" role="listbox">
      <button
        v-for="(opt, i) in filteredOptions"
        :key="opt.value ?? i"
        type="button"
        :class="['select-sheet__item', { 'is-selected': value == opt.value }]"
        role="option"
        :aria-selected="value == opt.value"
        @click="selectOption(opt.value)"
        :data-test="`${dataTestPrefix}-option-${opt.value}`"
      >
        <img
          v-if="opt.flag"
          :src="opt.flag"
          alt="flag"
          class="select-sheet__flag"
        />
        <span>{{ opt.label }}</span>
      </button>
      <div data-lang="no_data" v-if="filteredOptions.length === 0" class="select-sheet__empty">
        {{ $t("no_data") }}
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "SelectSheetDialogContent",
  props: {
    title: { type: String, default: "" },
    options: { type: Array, required: true },
    value: { default: null },
    valueField: { type: String, default: "id" },
    textField: { type: String, default: "name" },
    searchable: { type: Boolean, default: true },
    searchPlaceholder: { type: String, default: "" },
    dataTestPrefix: { type: String, default: "select-sheet" }
  },
  emits: ["action", "close"],
  data() {
    return {
      search: ""
    };
  },
  mounted() {
    if (this.searchable) {
      this.$nextTick(() => this.$refs.searchInput?.focus());
    }
  },
  computed: {
    normalizedOptions() {
      return (this.options || []).map(o => {
        if (o !== null && typeof o === "object") {
          return {
            value: o[this.valueField],
            label: o[this.textField] ?? o[this.valueField] ?? "",
            flag: o.flag,
            sort_order: o.sort_order || 0
          };
        }
        return { value: o, label: String(o), flag: null, sort_order: 0 };
      });
    },
    optionsSort() {
      return [...this.normalizedOptions].sort(
        (a, b) => a.sort_order - b.sort_order
      );
    },
    filteredOptions() {
      const q = String(this.search || "")
        .trim()
        .toLowerCase();
      if (!q) return this.optionsSort;
      return this.optionsSort.filter(o =>
        String(o.label || "")
          .toLowerCase()
          .includes(q)
      );
    }
  },
  methods: {
    selectOption(value) {
      this.$emit("action", { action: "confirm", value });
    }
  }
};
</script>

<style lang="scss" scoped>
.select-sheet {
  display: flex;
  flex-direction: column;
  max-height: 70vh;
}

.select-sheet__header {
  padding: 6px 0 14px;
  text-align: center;
}

.select-sheet__title {
  margin: 0;
  font-size: var(--font-body-lg);
  font-weight: 900;
  color: #2b2b2b;
}

.select-sheet__search {
  position: relative;
  margin: 8px 0 12px;
}

.select-sheet__search-input {
  width: 100%;
  height: 44px;
  padding: 0 40px 0 14px;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  background: #fff;
  color: #2b2b2b;
  font-size: var(--font-body);

  &::placeholder {
    color: #b1b1b1;
  }

  &:focus {
    outline: none;
    border-color: var(--primary-color, #2b2b2b);
  }
}

.select-sheet__search-icon {
  position: absolute;
  top: 50%;
  right: 14px;
  transform: translateY(-50%);
  color: #9a9a9a;
  pointer-events: none;
}

.select-sheet__list {
  flex: 1 1 auto;
  overflow-y: auto;
  margin: 0 -4px;
  padding: 4px;
  border-top: 1px solid #f0f0f0;
}

.select-sheet__item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 14px 12px;
  border: 0;
  background: transparent;
  color: #2b2b2b;
  font-size: var(--font-body);
  text-align: left;
  cursor: pointer;
  border-radius: 8px;

  &:hover {
    background: #f7f7f9;
  }

  &.is-selected {
    background: var(--secondary-color);
    font-weight: 700;
  }
}

.select-sheet__flag {
  width: 20px;
  height: 14px;
  flex-shrink: 0;
}

.select-sheet__empty {
  padding: 32px 12px;
  color: #9a9a9a;
  font-size: var(--font-body);
  text-align: center;
}
</style>
